import React, { useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { SBLogo } from "../components/WebLayout";
import SampleDataBanner from "../components/SampleDataBanner";
import { CREATOR_REFERRALS, CONTENT_KIT } from "../data/mockData";
import {
  LayoutGrid, Users, Wallet, Sparkles, BookOpen, Settings,
  Copy, Share2, Download, Trophy,
} from "lucide-react";

const NAV = [
  { icon: LayoutGrid, label: "Overview", active: true },
  { icon: Users, label: "Referrals" },
  { icon: Wallet, label: "Payouts" },
  { icon: Sparkles, label: "Promotional Tools" },
  { icon: BookOpen, label: "Resources" },
  { icon: Settings, label: "Settings" },
];

const STATS = [
  { label: "Link Clicks", value: "1,248" },
  { label: "Signups", value: "342" },
  { label: "Qualified Referrals", value: "18" },
  { label: "Revenue Generated", value: "$12,450", tone: "gold" },
  { label: "Pending Commission", value: "$650" },
  { label: "Paid Commission", value: "$1,800" },
];

const STATUS_PILL = { Qualified: "sb-pill-green", "Signed Up": "sb-pill-blue", Pending: "sb-pill-amber" };

export default function CreatorDashboard() {
  const link = "spotbounty.com/c/jordan";
  const code = "JORDAN10";
  const [copied, setCopied] = useState("");
  const copy = (v, k) => {
    navigator.clipboard?.writeText(v).catch(() => {});
    setCopied(k); setTimeout(() => setCopied(""), 1500);
    toast.success("Copied.");
  };

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#2A2F34] bg-[#0B0E11] p-5 sticky top-0 h-screen">
        <Link to="/"><SBLogo size={18} /></Link>
        <div className="mt-8 sb-eyebrow">Creator</div>
        <nav className="mt-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.label} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${n.active ? "bg-[#FFC107]/10 text-[#FFC107]" : "text-[#A1A1AA] hover:text-white hover:bg-white/5"}`}>
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 p-6 space-y-8">
        <SampleDataBanner />
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="sb-eyebrow">Creator Overview</div>
            <h1 className="mt-1 text-[28px] font-black">Hey Jordan 👋 — <span className="text-[#FFC107]">your funnel is heating up</span></h1>
          </div>
          <div className="sb-card p-3 flex items-center gap-3">
            <Trophy size={20} className="text-[#FFC107]" />
            <div>
              <div className="sb-eyebrow" style={{ fontSize: 10 }}>Creator Tier</div>
              <div className="text-[14px] font-black">Gold Partner</div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {STATS.map((s) => (
            <div key={s.label} className="sb-card p-5">
              <div className="sb-eyebrow" style={{ fontSize: 10 }}>{s.label}</div>
              <div className={`mt-3 text-[24px] font-black ${s.tone === "gold" ? "text-[#FFC107]" : "text-white"}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <div className="sb-card p-6">
            <div className="sb-eyebrow">Referral Tools</div>
            <div className="mt-4 flex items-center gap-2">
              <div className="sb-mono text-[13px] flex-1 truncate">{link}</div>
              <button onClick={() => copy(`https://${link}`, "l")} className="sb-pill sb-pill-gray"><Copy size={12} /> {copied === "l" ? "Copied" : "Copy"}</button>
              <button className="sb-pill sb-pill-gray"><Share2 size={12} /> Share</button>
            </div>
            <div className="mt-6 flex items-center gap-6">
              <div className="p-2 bg-white rounded-md">
                <QRCode value={`https://${link}`} size={100} bgColor="#ffffff" fgColor="#080B0D" />
              </div>
              <div>
                <div className="sb-eyebrow" style={{ fontSize: 10 }}>Unique Code</div>
                <div className="mt-1 text-[26px] font-black tracking-widest">{code}</div>
                <button onClick={() => copy(code, "c")} className="mt-2 sb-pill sb-pill-gray"><Copy size={12} /> Copy code</button>
              </div>
            </div>
          </div>

          <div className="sb-card p-6">
            <div className="sb-eyebrow">Recent Referrals</div>
            <div className="mt-3 divide-y divide-[#2A2F34]">
              {CREATOR_REFERRALS.map((r) => (
                <div key={r.id} className="py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#FFC107]/10 text-[#FFC107] flex items-center justify-center text-[11px] font-black">{r.name.split(" ")[0][0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold">{r.name}</div>
                    <div className="text-[11px] text-[#A1A1AA]">{r.platform}</div>
                  </div>
                  <span className={`sb-pill ${STATUS_PILL[r.status] || "sb-pill-gray"}`} style={{ fontSize: 10 }}>{r.status}</span>
                  <div className="text-[13px] font-black text-[#FFC107] w-14 text-right">{r.amount ? `+$${r.amount}` : "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section>
          <div className="sb-eyebrow">Content Kit</div>
          <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {CONTENT_KIT.map((c) => (
              <div key={c.key} className="sb-card p-5">
                <div className="w-10 h-10 rounded-lg bg-[#FFC107]/10 text-[#FFC107] flex items-center justify-center font-black text-[13px]">{c.label[0]}</div>
                <div className="mt-3 font-bold text-[14px]">{c.label}</div>
                <div className="text-[12px] text-[#A1A1AA] mt-1">{c.desc}</div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button onClick={() => { toast.success(`${c.label} copied.`); }} className="sb-pill sb-pill-gray text-[10px]"><Copy size={10} /> Copy</button>
                  <button className="sb-pill sb-pill-gray text-[10px]"><Download size={10} /> Download</button>
                  <button className="sb-pill sb-pill-gray text-[10px]"><Share2 size={10} /> Share</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
